import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  StackDivider,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useState } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";

export type CMFormInputPasswordProps = {
  /**
   * falseの場合、現在のパスワードを非表示
   */
  isDisplayCurrentPassword?: boolean;

  labelCurrentPassword?: string;
  labelNewPassword?: string;

  /**
   * 新しいパスワード(確認)のラベル
   */
  labelVerifyPassword?: string;

  labelDisplayChangeButton?: { show: string; hide: string };

  placeholder?: string;

  /**
   * 初期値。
   */
  initialValue: {
    currentPassword: string;
    newPassword: string;
    verifyPassword: string;
  };

  onChange?: (
    currentPassword: string,
    newPassword: string,
    verifyPassword: string,
  ) => void;
} & ComponentStyleProps;

/**
 * パスワードを入力するフォームを表示するコンポーネント
 */
export const CMFormInputPassword = ({
  isDisplayCurrentPassword = true,
  labelCurrentPassword,
  labelNewPassword,

  labelVerifyPassword,
  labelDisplayChangeButton,
  placeholder,
  initialValue,
  onChange,

  sx,
  ...rest
}: CMFormInputPasswordProps) => {
  const styles = useMultiStyleConfig("CMFormInputPasswordStyles");

  const [currentPassword, setCurrentPassword] = useState<string>(
    isDisplayCurrentPassword ? initialValue.currentPassword : "",
  );
  const [newPassword, setNewPassword] = useState(initialValue.newPassword);
  const [verifyPassword, setVerifyPassword] = useState(
    initialValue.verifyPassword,
  );

  const [currentPwShow, setCurrentPwShow] = useState(false);
  const [newPwShow, setNewPwShow] = useState(false);
  const [verifyPwShow, setVerifyPwShow] = useState(false);

  /**
   * 表示切替ボタンの制御
   * onCurrentPwShow 現在のパスワード表示切替ボタンのイベント
   * onNewPwShow 新しいパスワード表示切替ボタンのイベント
   * onVerifyPwShow 新しいパスワード・確認表示切替ボタンのイベント
   */
  const _handleClick = useCallback(
    (
      onCurrentPwShow: React.MouseEvent<HTMLButtonElement> | null,
      onNewPwShow: React.MouseEvent<HTMLButtonElement> | null,
      onVerifyPwShow: React.MouseEvent<HTMLButtonElement> | null,
    ) => {
      if (onCurrentPwShow) {
        setCurrentPwShow(!currentPwShow);
      } else if (onNewPwShow) {
        setNewPwShow(!newPwShow);
      } else if (onVerifyPwShow) {
        setVerifyPwShow(!verifyPwShow);
      }
    },
    [currentPwShow, newPwShow, verifyPwShow],
  );

  /**
   * inputの値の変更を親元のコンポーネントに返却する
   * currentPwValue 入力された現在のパスワード
   * newPwValue 入力された新しいパスワード
   * verifyPwValue 入力された新しいパスワード・確認
   */
  const _onChange = useCallback(
    (
      currentPwValue: React.ChangeEvent<HTMLInputElement> | null,
      newPwValue: React.ChangeEvent<HTMLInputElement> | null,
      verifyPwValue: React.ChangeEvent<HTMLInputElement> | null,
    ) => {
      // 呼び出し元に変更を通知する
      if (currentPwValue) {
        setCurrentPassword(currentPwValue.target.value);
        onChange?.(currentPwValue.target.value, newPassword, verifyPassword);
      } else if (newPwValue) {
        setNewPassword(newPwValue.target.value);
        onChange?.(currentPassword, newPwValue.target.value, verifyPassword);
      } else if (verifyPwValue) {
        setVerifyPassword(verifyPwValue.target.value);
        onChange?.(currentPassword, newPassword, verifyPwValue.target.value);
      }
    },
    [currentPassword, newPassword, onChange, verifyPassword],
  );

  return (
    <Stack
      spacing={0}
      divider={<StackDivider sx={styles.divider} />}
      sx={{ ...styles.container, ...sx }}
      {...rest}
    >
      {isDisplayCurrentPassword ? (
        <Box sx={styles.box}>
          <Text sx={styles.header}>{labelCurrentPassword}</Text>
          <InputGroup>
            <Input
              type={currentPwShow ? "text" : "password"}
              placeholder={placeholder}
              value={currentPassword}
              onChange={(e) => _onChange(e, null, null)}
              sx={styles.input}
            />
            <InputRightElement sx={styles.inputRightElement}>
              <Button
                onClick={(e) => _handleClick(e, null, null)}
                sx={styles.inputButton}
              >
                {currentPwShow
                  ? labelDisplayChangeButton?.hide
                  : labelDisplayChangeButton?.show}
              </Button>
            </InputRightElement>
          </InputGroup>
        </Box>
      ) : undefined}
      <Box sx={styles.box}>
        <Text sx={styles.header}>{labelNewPassword}</Text>
        <InputGroup>
          <Input
            type={newPwShow ? "text" : "password"}
            placeholder={placeholder}
            value={newPassword}
            onChange={(e) => _onChange(null, e, null)}
            sx={styles.input}
          />
          <InputRightElement sx={styles.inputRightElement}>
            <Button
              onClick={(e) => _handleClick(null, e, null)}
              sx={styles.inputButton}
            >
              {newPwShow
                ? labelDisplayChangeButton?.hide
                : labelDisplayChangeButton?.show}
            </Button>
          </InputRightElement>
        </InputGroup>
      </Box>
      <Box sx={styles.box}>
        <Text sx={styles.header}>{labelVerifyPassword}</Text>
        <InputGroup>
          <Input
            type={verifyPwShow ? "text" : "password"}
            placeholder={placeholder}
            value={verifyPassword}
            onChange={(e) => _onChange(null, null, e)}
            sx={styles.input}
          />
          <InputRightElement sx={styles.inputRightElement}>
            <Button
              onClick={(e) => _handleClick(null, null, e)}
              sx={styles.inputButton}
            >
              {verifyPwShow
                ? labelDisplayChangeButton?.hide
                : labelDisplayChangeButton?.show}
            </Button>
          </InputRightElement>
        </InputGroup>
      </Box>
    </Stack>
  );
};

export const CMFormInputPasswordStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "divider",
    "box",
    "header",
    "input",
    "inputRightElement",
    "inputButton",
  ],
  baseStyle: {
    container: {
      bg: "white",
      border: "solid 1px",
      borderColor: "gray.400",
      borderRadius: "6px",
    },
    divider: {
      borderColor: "gray.200",
    },
    box: {
      p: "20px",
    },
    header: {
      fontWeight: "700",
      fontSize: "14px",
      lineHeight: "20px",
      color: "gray.900",
      pb: "7px",
    },
    input: {
      h: "32px",
      borderColor: "gray.200",
      fontWeight: "400",
      fontSize: "14px",
      lineHeight: "20px",
      color: "gray.900",
    },
    inputRightElement: { h: "100%" },
    inputButton: {
      h: "20px",
      bg: "gray.300",
      mr: "5px",
      fontWeight: "500",
      fontSize: "12px",
      lineHeight: "16px",
      color: "gray.500",
    },
  },
};
